<template>
    <Form @submit="saveOwner" :key="createMode">
        <div class="owner-form flex flex-column gap-4">
            <div class="owner-data flex gap-6">
                <div class="person-box flex flex-column w-6 gap-3">
                    <CustomInputTextSuffix ref="suffix" name="suffix" :label="$t('message.owner.suffix')" class="w-10"
                        v-model="editedOwner.suffix" :country-id="editedOwner.address.countryId" noPlaceholder @input="toUpperCase"
                        :customErrorMessage="getSuffixMessage(editedOwner.suffix)" :countries="countries"/>
                    <CustomInputText name="fullName" :label="$t('message.owner.fullName')" class="w-10"
                                     v-model="editedOwner.fullName" noPlaceholder required
                                     :customErrorMessage="getRequiredMessage(editedOwner.fullName)"/>
                    <CustomInputText name="phone" :label="$t('message.owner.phoneNumber')" class="w-10"
                                     v-model="editedOwner.phone" noPlaceholder/>
                    <CustomInputText name="email" :label="$t('message.owner.email')" class="w-10"
                                     v-model="editedOwner.email" noPlaceholder/>
                    <CustomInputText name="nationalId" :label="$t('message.owner.nationalIdNumber')" class="w-10"
                                       v-model="editedOwner.nationalIdNumber" noPlaceholder/>
                    <CustomInputText name="farmName" :label="$t('message.owner.farmName')" class="w-10"
                                     v-model="editedOwner.farmName" noPlaceholder/>
                </div>
                <div class="w-6 address-box-outer">
                    <span class="header">Address</span>
                    <div class="address-box w-full flex flex-column gap-3 mt-3">
                        <div class="flex flex-column gap-3">
                            <CustomSelectOne name="country" :label="$t('message.owner.address.country')"
                                             v-model="editedOwner.address.countryId" noPlaceholder
                                             :items="countries" itemValue="id" itemLabel="name"
                                             required :customErrorMessage="getRequiredMessage(editedOwner.address.countryId)"/>
                            <div class="flex gap-3">
                                <CustomSelectOneCity v-if="editedOwner.address.countryId === 1"
                                                     name="city" :label="$t('message.owner.address.city')"
                                                     v-model="editedOwner.address.city" noPlaceholder
                                                     required :customErrorMessage="getRequiredMessage(editedOwner.address.city)" />
                                <CustomInputText v-else name="city" :label="$t('message.owner.address.city')"
                                                 v-model="editedOwner.address.city" noPlaceholder
                                                 required :customErrorMessage="getRequiredMessage(editedOwner.address.city)" />
                                <CustomInputText name="postalCode" :label="$t('message.owner.address.postalCode')"
                                                 v-model="editedOwner.address.postalCode" noPlaceholder/>
                            </div>
                            <CustomInputText name="street" :label="$t('message.owner.address.street')"
                                             v-model="editedOwner.address.street" noPlaceholder/>
                            <div class="flex gap-3">
                                <CustomInputText name="propertyNumber" :label="$t('message.owner.address.propertyNumber')"
                                                 v-model="editedOwner.address.propertyNumber" noPlaceholder />
                                <CustomInputText name="apartmentNumber" :label="$t('message.owner.address.apartmentNumber')"
                                                 v-model="editedOwner.address.apartmentNumber" noPlaceholder/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CustomInputTextArea name="notes" :label="$t('message.owner.notes')" v-model="editedOwner.notes" />
            <div class="user-buttons flex align-self-center gap-4 mb-5">
                <CustomButton :label="$t('message.other.save')" type="submit" />
                <CustomButton v-if="createMode" :label="$t('message.other.cancel')" secondary
                              @click="hideForm" />
                <CustomButton v-else :label="$t('message.other.delete')" secondary
                              @click="deleteOwner" />
            </div>
        </div>
    </Form>
</template>

<script>
    import {Form} from "vee-validate";
    import {
        createOrUpdateOwner,
        deleteOwner,
    } from "@/swagger/vue-api-client";
    import CustomInputText from "@/components/form/CustomInputText";
    import CustomCheckbox from "@/components/form/CustomCheckbox.vue";
    import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
    import CustomSelectOne from "@/components/form/inner/CustomSelectOne.vue";
    import CustomInputTextArea from "@/components/form/CustomInputTextArea.vue";
    import CustomButton from "@/components/form/CustomButton.vue";
    import { ValidateUtils } from "@/utils/ValidateUtils";
    import CustomInputTextSuffix from "@/components/form/CustomInputTextSuffix.vue";
    import CustomSelectOneCity from "@/components/form/CustomSelectOneCity.vue";

    export default {
        name: "OwnerForm",

        components: {
            CustomSelectOneCity,
            CustomInputTextSuffix,
            CustomInputNumber,
            CustomCheckbox,
            CustomInputText,
            Form,
            CustomSelectOne,
            CustomInputTextArea,
            CustomButton,
        },

        props: {
            selectedOwner: {
                type: Object,
                required: true,
            },
            countries: {
                type: Array,
                required: true,
            },
            ownerAddDialog: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                editedOwner: {
                    suffix: null,
                    fullName: null,
                    business: false,
                    businessNumber: null,
                    nationalIdNumber: null,
                    phone: null,
                    address: {
                        street: null,
                        postalCode: null,
                        city: null,
                        region: null,
                        countryId: 1,
                        propertyNumber: null,
                        apartmentNumber: null,
                    },
                    owner: false,
                    breeder: false,
                    notes: null,
                },
                createMode: true,
                showErrorMessages: false,
            };
        },

        emits: ["change-element", "createMode", "hideForm", "closeDialog", "addedNewOwner"],

        mounted() {
            this.$emit("createMode", this.createMode);
        },

        methods: {
            validateData() {
                let isCorrect = ValidateUtils.validateNotEmpty(this.editedOwner, ["fullName"]);
                isCorrect = ValidateUtils.validateNotEmpty(this.editedOwner.address, ["countryId"]) && isCorrect;
                isCorrect = this.editedOwner.address.city && isCorrect;
                if (ValidateUtils.validateNotEmpty(this.editedOwner, ["suffix"])) {
                    if (this.editedOwner.address?.countryId && this.editedOwner.address.countryId === 1 && this.editedOwner.suffix.toUpperCase()[0] === "L") {
                        isCorrect = true && isCorrect;
                    } else if (this.editedOwner.address?.countryId && this.editedOwner.address.countryId !== 1 && this.editedOwner.suffix.toUpperCase()[0] === "F") {
                        isCorrect = true && isCorrect;
                    } else {
                        isCorrect = false && isCorrect;
                    }
                }
                this.showErrorMessages = !isCorrect;
                return isCorrect;
            },
            getRequiredMessage(value) {
                if (this.showErrorMessages && (value === null || value === "")) {
                    return this.$t("message.validations.required");
                }
                return "";
            },
            getSuffixMessage(value) {
                if (this.showErrorMessages && value !== null && value !== "" && this.editedOwner?.address?.countryId === 1 && value.toUpperCase()[0] !== "L") {
                    return this.$t("message.validations.suffixL");
                }
                if (this.showErrorMessages && value !== null && value !== "" && this.editedOwner?.address?.countryId !== 1 && value?.toUpperCase()[0] !== "F") {
                    return this.$t("message.validations.suffixF");
                }
                return "";
            },
            notifyChange() {
                this.$emit("change-element");
                this.$toast.add({
                    severity: "success",
                    summary: this.$t('message.other.success'),
                    detail: this.$t('message.other.dataSaved'),
                    life: 3000,
                });
                if (this.ownerAddDialog) {
                    this.$emit("closeDialog");
                    this.$emit("addedNewOwner");
                }
            },
            notifyDelete() {
                this.$emit("change-element");
                this.$toast.add({
                    severity: "success",
                    summary: this.$t('message.other.success'),
                    detail: this.$t('message.other.elementDeleted'),
                    life: 3000,
                });
            },
            notifyError(error) {
                const message = error.response?.data?.message || this.$t('unknownError');
                this.$toast.add({
                    severity: "error",
                    summary: this.$t('message.other.error'),
                    detail: message,
                    life: 5000,
                });
            },
            saveOwner() {
                this.editedOwner.suffix = this.$refs.suffix.getFullSuffix();
                if (this.validateData()) {
                    createOrUpdateOwner({body: this.editedOwner})
                        .then(() => {
                            this.notifyChange();
                        })
                        .catch((error) => {
                            this.notifyError(error);
                        });
                }
            },
            deleteOwner() {
                deleteOwner({id: this.editedOwner.id})
                    .then(() => {
                        this.notifyDelete();
                        this.showErrorMessage = false;
                    })
                    .catch((error) => {
                        this.notifyError(error);
                    });
            },
            hideForm() {
                if (this.ownerAddDialog) {
                    this.$emit("closeDialog");
                } else {
                    this.$emit("hideForm");
                }
            },
            toUpperCase(e) {
                this.editedOwner.suffix = e.target.value.toUpperCase()
            }
        },

        watch: {
            selectedOwner(newValue) {
                this.editedOwner = _.cloneDeep(newValue);
                if (this.editedOwner.address === null) {
                    this.editedOwner.address = {
                        street: null,
                        postalCode: null,
                        city: null,
                        region: null,
                        countryId: 1,
                        propertyNumber: null,
                        apartmentNumber: null,
                    };
                }
                if (this.editedOwner.suffix !== null) {
                    this.editedOwner.suffix = this.editedOwner.suffix.slice(1);
                }
                this.createMode = newValue.id === null || newValue.id === undefined || isNaN(newValue.id);
                this.$emit("createMode", this.createMode);
            },
        },
    };
</script>

<style lang="scss">
    .owner-form {
        .custom-input-text, .custom-input-number, .custom-select-one {
            padding-left: 170px;
            .custom-label {
                z-index: 999;
                position: absolute;
                transform: translateX(-100%);
                left: 160px;
                text-align: right;
            }
        }
        .owner-data .person-box {
            .custom-input-text, .custom-input-number, .custom-select-one {
                padding-left: 185px;
                .custom-label {
                    left: 175px;
                }
            }
            @media screen and (max-width: 450px) {
                .custom-input-text, .custom-input-number, .custom-select-one {
                    padding-left: 155px;
                    .custom-label {
                        left: 145px;
                    }
                }
            }
        }
        .header {
            font-size: 1.5rem;
        }
        .address-box {
            background-color: var(--primary-color);
            padding: 1.5rem;
            border-radius: var(--border-radius);
            .p-inputtext, .p-dropdown, .p-inputtext:hover, .p-dropdown:not(.p-disabled):hover {
                background-color: var(--primary-color);
                border-color: var(--text-color);
                .p-dropdown-trigger {
                    color: var(--text-color);
                }
            }
        }
        .owner-data {
            @media screen and (max-width: 1580px) {
                gap: 0 !important;
            }
            @media screen and (max-width: 1300px) {
                flex-direction: column;
                margin: 0 auto;
                .person-box, .address-box-outer {
                    width: 100% !important;
                }
            }
            @media screen and (max-width: 750px) {
                .address-box div {
                    flex-direction: column;
                    justify-content: center;
                }
                .address-box div.p-dropdown {
                    flex-direction: row;
                }
            }
        }
    }
</style>
